import { writable } from 'svelte/store';

import type { DashboardRecord } from 'src/components/Dashboards/dash-types';
import type {
	DashEnergyWidgetConfig,
	DashEnergyWidgetTypes,
} from 'src/components/Dashboards/dash-energy-types';
import {
	ALL_ENERGY_WIDGETS_DASHBOARD_ID,
	ALL_ENERGY_WIDGETS_DASHBOARD_ROUTE,
	type EnergyV2Widget,
} from './energy-v2-types';

export type EnergyWidgetPreviewState = 'live' | 'sample' | 'empty' | 'setup' | 'partial' | 'error';

export const energyWidgetPreviewState = writable<EnergyWidgetPreviewState>('live');

type GalleryWidgetDefinition = {
	id: string;
	type: DashEnergyWidgetTypes;
	title: string;
	energyConfig: DashEnergyWidgetConfig;
};

const definitions: GalleryWidgetDefinition[] = [
	{
		id: 'all-energy-info',
		type: 'dashEnergyInfo',
		title: 'Live energy overview',
		energyConfig: {
			period: '24_hours',
			infoCategories: ['load', 'solar', 'selfPowered', 'gridImport'],
			showTariffBar: false,
		},
	},
	{
		id: 'all-energy-info-period',
		type: 'dashEnergyInfo',
		title: 'Period energy overview',
		energyConfig: {
			period: '24_hours',
			infoCategories: ['periodConsumption', 'periodGeneration', 'tariff', 'currentTariff'],
			showTariffBar: true,
		},
	},
	{
		id: 'all-energy-info-insights',
		type: 'dashEnergyInfo',
		title: 'Energy usage overview',
		energyConfig: {
			period: '24_hours',
			infoCategories: ['offPeak', 'topConsumer', 'standby', 'peakDemand'],
			showTariffBar: true,
		},
	},
	{
		id: 'all-energy-kpi-live',
		type: 'dashEnergyKpiLive',
		title: 'Live energy KPIs',
		energyConfig: { period: 'live' },
	},
	{
		id: 'all-energy-kpi-period',
		type: 'dashEnergyKpiPeriod',
		title: 'Period energy KPIs',
		energyConfig: { period: '24_hours', showTariffBar: true },
	},
	{
		id: 'all-energy-top-consumers',
		type: 'dashEnergyTopConsumers',
		title: 'Top consumers',
		energyConfig: { period: 'live', limit: 5, groupBy: 'device' },
	},
	{
		id: 'all-energy-consumer-mix',
		type: 'dashEnergyConsumerMix',
		title: 'Consumer mix',
		energyConfig: { period: 'live', minSharePercent: 3, showLegend: true },
	},
	{
		id: 'all-energy-room-chart',
		type: 'dashEnergyRoomChart',
		title: 'Room consumption overview',
		energyConfig: { period: '7_days', chartType: 'bar' },
	},
	{
		id: 'all-energy-flow',
		type: 'dashEnergyFlow',
		title: 'Energy flow',
		energyConfig: {
			period: 'live',
			showSupply: true,
			supplyMode: 'auto',
			showRooms: true,
			showDevices: true,
			maxRoomNodes: 6,
			maxDeviceNodes: 8,
		},
	},
	{
		id: 'all-energy-heatmap-full',
		type: 'dashEnergyHeatmap',
		title: 'Yearly Heatmap',
		energyConfig: {
			period: 'year',
			heatmapScope: 'all',
			heatmapMetric: 'consumption',
			heatmapVariant: 'full',
			allowInWidgetMetricToggle: false,
			showPeriodTotal: true,
		},
	},
	{
		id: 'all-energy-heatmap-minimal',
		type: 'dashEnergyHeatmap',
		title: 'Monthly Heatmap',
		energyConfig: {
			period: 'year',
			heatmapScope: 'all',
			heatmapMetric: 'consumption',
			heatmapVariant: 'minimal',
			allowInWidgetMetricToggle: false,
			showPeriodTotal: true,
		},
	},
	{
		id: 'all-energy-peak-overview',
		type: 'dashEnergyPeak',
		title: 'Peak Power Overview',
		energyConfig: { period: 'live', peakVariant: 'overview' },
	},
	{
		id: 'all-energy-peak-meter',
		type: 'dashEnergyPeak',
		title: 'Peak Power by Meter',
		energyConfig: { period: 'live', peakVariant: 'byMeter' },
	},
	{
		id: 'all-energy-peak-phase',
		type: 'dashEnergyPeak',
		title: 'Phase Peak Balance',
		energyConfig: { period: 'live', peakVariant: 'phaseBalance' },
	},
	{
		id: 'all-energy-insights',
		type: 'dashEnergyInsights',
		title: 'Insights',
		energyConfig: { period: '24_hours' },
	},
	{
		id: 'all-energy-generation',
		type: 'dashEnergyGeneration',
		title: 'Generation vs consumption',
		energyConfig: { period: '24_hours' },
	},
	{
		id: 'all-energy-distribution',
		type: 'dashEnergyPvDistribution',
		title: 'Live energy distribution',
		energyConfig: { period: 'live' },
	},
	{
		id: 'all-energy-tariff',
		type: 'dashEnergyTariff',
		title: 'Tariff pressure',
		energyConfig: { period: '24_hours' },
	},
	{
		id: 'all-energy-phase',
		type: 'dashEnergyPhase',
		title: 'Phase health',
		energyConfig: { period: 'live' },
	},
	{
		id: 'all-energy-environment',
		type: 'dashEnergyEnvironmental',
		title: 'Environment',
		energyConfig: { period: 'live' },
	},
	{
		id: 'all-energy-device-trend',
		type: 'dashEnergyDeviceTrend',
		title: 'Device energy trend',
		energyConfig: { period: '24_hours' },
	},
	{
		id: 'all-energy-room-charts',
		type: 'dashEnergyRoomCharts',
		title: 'Room consumption',
		energyConfig: { period: '7_days' },
	},
	{
		id: 'all-energy-room-distribution',
		type: 'dashEnergyRoomDistribution',
		title: 'Energy Distribution By Room',
		energyConfig: { period: '7_days' },
	},
];

const columnPositions = { left: 0, right: 0 };

export const ALL_ENERGY_WIDGETS: EnergyV2Widget[] = definitions.map((definition, index) => {
	const col = index % 2 === 0 ? 'left' : 'right';
	return {
		...definition,
		col,
		position: columnPositions[col]++,
		showWidgetTitle: true,
		cards: [{ id: definition.id, _type: 'energy', position: 0 }],
	};
});

export const ALL_ENERGY_WIDGETS_DASHBOARD: DashboardRecord = {
	id: ALL_ENERGY_WIDGETS_DASHBOARD_ID,
	name: 'All Energy Widgets',
	text: 'All Energy Widgets',
	position: 2,
	route: ALL_ENERGY_WIDGETS_DASHBOARD_ROUTE,
	protected: true,
	schemaVersion: 1,
	icon: 'fas fa-grid-2',
	backgroundColor: 'color-0',
	dashWidgets: ALL_ENERGY_WIDGETS,
};

export const isAllEnergyWidgetsDashboardId = (id: string | number | undefined | null): boolean =>
	Number(id) === ALL_ENERGY_WIDGETS_DASHBOARD_ID;
